import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Award, Target, Users, Heart } from "lucide-react";

const About = () => {
  return (
    <main data-testid="about-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <p className="text-[#56C6C6] font-mono text-sm tracking-widest uppercase mb-4">
              About V1 Athletics
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight uppercase leading-tight mb-8">
              Building the <span className="text-[#56C6C6]">Future</span> of Athletic Development
            </h1>
            <p className="text-xl text-zinc-400 leading-relaxed">
              V1 Athletics was founded on the belief that every young athlete deserves access to 
              professional-grade training, guidance, and support. Our mission is to develop 
              not just better athletes, but better humans.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 bg-[#121212]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#56C6C6] font-mono text-sm tracking-widest uppercase mb-4">
                Our Mission
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight uppercase mb-6">
                LTAD Principles in Action
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-6">
                We align our methodology with modern Long-Term Athlete Development (LTAD) principles, 
                recognizing that athletic development is a journey that spans years, not weeks.
              </p>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Our four-pillar approach ensures that athletes develop the complete toolkit needed 
                for success: physical capability, nutritional knowledge, recovery strategies, and 
                mental resilience.
              </p>
              <Link to="/contact">
                <Button
                  data-testid="mission-cta"
                  className="bg-[#56C6C6] text-black hover:bg-[#45B5B5] font-bold uppercase tracking-wider px-6 py-5"
                >
                  Learn About Our Programs
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div data-testid="value-0" className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#56C6C6]/30 transition-colors">
                <Target className="w-8 h-8 text-[#56C6C6] mb-4" strokeWidth={1.5} />
                <h4 className="text-white font-bold uppercase tracking-wide mb-2 text-sm">Long-Term Focus</h4>
                <p className="text-zinc-500 text-xs leading-relaxed">We prioritize sustainable development over quick results.</p>
              </div>
              <div data-testid="value-1" className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#56C6C6]/30 transition-colors">
                <Users className="w-8 h-8 text-[#56C6C6] mb-4" strokeWidth={1.5} />
                <h4 className="text-white font-bold uppercase tracking-wide mb-2 text-sm">Holistic Approach</h4>
                <p className="text-zinc-500 text-xs leading-relaxed">Training, nutrition, recovery, and mindset work together.</p>
              </div>
              <div data-testid="value-2" className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#56C6C6]/30 transition-colors">
                <Heart className="w-8 h-8 text-[#56C6C6] mb-4" strokeWidth={1.5} />
                <h4 className="text-white font-bold uppercase tracking-wide mb-2 text-sm">Athlete-Centered</h4>
                <p className="text-zinc-500 text-xs leading-relaxed">Every program is tailored to individual needs and goals.</p>
              </div>
              <div data-testid="value-3" className="bg-[#0A0A0A] border border-white/5 p-6 hover:border-[#56C6C6]/30 transition-colors">
                <Award className="w-8 h-8 text-[#56C6C6] mb-4" strokeWidth={1.5} />
                <h4 className="text-white font-bold uppercase tracking-wide mb-2 text-sm">Excellence</h4>
                <p className="text-zinc-500 text-xs leading-relaxed">Highest standards in coaching and athlete support.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section data-testid="founders-section" className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="container-custom">
          <div className="mb-16 text-center">
            <p className="text-[#56C6C6] font-mono text-sm tracking-widest uppercase mb-4">
              Meet the Team
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight uppercase">
              Our Founders
            </h2>
          </div>

          <div className="space-y-24">
            {/* Liam Sullivan */}
            <div data-testid="founder-0" className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="aspect-[3/4] max-w-md mx-auto lg:mx-0 relative overflow-hidden bg-zinc-900">
                  <img
                    src="https://customer-assets.emergentagent.com/job_athletedev/artifacts/9x0xlsau_Liam%20photo.jpg"
                    alt="Liam Sullivan"
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  />
                  <div className="absolute inset-0 border border-white/10" />
                </div>
                <div className="absolute -bottom-4 left-4 bg-[#56C6C6] px-6 py-3">
                  <p className="text-black font-black text-lg uppercase tracking-tight">Liam</p>
                </div>
              </div>
              <div>
                <p className="text-[#56C6C6] font-mono text-xs tracking-widest uppercase mb-2">
                  Co-Founder & Head Coach
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
                  Liam Sullivan
                </h3>
                <p className="text-zinc-400 leading-relaxed mb-8">
                  Liam is a NASM certified personal trainer based in White Rock, BC, with extensive experience 
                  coaching athletes across multiple sports programs. A former rugby player and current triathlete, 
                  Liam has dedicated his career to helping young athletes develop their physical capabilities and 
                  reach their full potential. His structured approach to training has helped hundreds of athletes 
                  improve their performance in rugby, soccer, tennis, and various other sports. Liam believes that 
                  consistent, science-based training combined with proper recovery and nutrition creates the 
                  foundation for athletic excellence.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white/5 border border-white/10 text-zinc-400 text-xs uppercase tracking-wider">NASM Certified</span>
                  <span className="px-3 py-1 bg-white/5 border border-white/10 text-zinc-400 text-xs uppercase tracking-wider">Triathlete</span>
                  <span className="px-3 py-1 bg-white/5 border border-white/10 text-zinc-400 text-xs uppercase tracking-wider">Rugby Coach</span>
                  <span className="px-3 py-1 bg-white/5 border border-white/10 text-zinc-400 text-xs uppercase tracking-wider">Multi-Sport Specialist</span>
                </div>
              </div>
            </div>

            {/* Lewis Boswell */}
            <div data-testid="founder-1" className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="lg:order-2 relative">
                <div className="aspect-[3/4] max-w-md mx-auto lg:mx-0 lg:ml-auto relative overflow-hidden bg-zinc-900">
                  <img
                    src="https://customer-assets.emergentagent.com/job_athletedev/artifacts/ntynxvue_LEWIS.jpg"
                    alt="Lewis Boswell"
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  />
                  <div className="absolute inset-0 border border-white/10" />
                </div>
                <div className="absolute -bottom-4 right-4 lg:left-auto lg:right-4 bg-[#56C6C6] px-6 py-3">
                  <p className="text-black font-black text-lg uppercase tracking-tight">Lewis</p>
                </div>
              </div>
              <div className="lg:order-1">
                <p className="text-[#56C6C6] font-mono text-xs tracking-widest uppercase mb-2">
                  Co-Founder & Performance Director
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
                  Lewis Boswell
                </h3>
                <p className="text-zinc-400 leading-relaxed mb-8">
                  Lewis brings extensive experience in athletic performance and coaching to V1 Athletics. 
                  With a background in sports science and performance optimization, he focuses on developing 
                  comprehensive training programs that align with Long-Term Athlete Development principles. 
                  His expertise in recovery protocols and mindset coaching helps athletes build sustainable 
                  careers in their chosen sports.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white/5 border border-white/10 text-zinc-400 text-xs uppercase tracking-wider">Performance Specialist</span>
                  <span className="px-3 py-1 bg-white/5 border border-white/10 text-zinc-400 text-xs uppercase tracking-wider">LTAD Expert</span>
                  <span className="px-3 py-1 bg-white/5 border border-white/10 text-zinc-400 text-xs uppercase tracking-wider">Recovery Protocol Designer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#121212]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight uppercase mb-6">
              Ready to Train with <span className="text-[#56C6C6]">V1</span>?
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Connect with our team to learn how we can support your athletic journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="about-cta-contact"
                  className="bg-[#56C6C6] text-black hover:bg-[#45B5B5] font-bold uppercase tracking-wider px-8 py-6 text-sm w-full sm:w-auto"
                >
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
